import { api } from '../api.js';
import { getUser } from '../auth.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { escapeHtml, sprintOptionsHtml, nextSprintNumber, renderMarkdown, TYPE_LABELS } from '../utils.js';
import { renderMemberCombo, createMemberCombo } from './member-combo.js';
import { attachImageHandlers } from '../lib/image-paste.js';
import { attachEmbedControls } from '../lib/embed-menu.js';
import { openTrelloCardPicker } from './trello-card-picker.js';
import { openGmailThreadPicker } from './gmail-thread-picker.js';
import { openJiraIssuePicker } from './jira-issue-picker.js';
import { closeImportPicker } from '../lib/import-picker-shell.js';
import { attachTagTypeahead } from '../lib/tag-typeahead.js';
import { attachDependencyTypeahead } from './dependency-typeahead.js';
import { loadNewTaskDraft, saveNewTaskDraft, clearNewTaskDraft, attachmentDeletePath } from '../lib/task-draft.js';
import { groupLabelSingular } from '../lib/task-group-label.js';
import { sprintsEnabled, defaultSprintNumber } from '../lib/sprints-enabled.js';
import { loadProject } from '../lib/project.js';
import { loadStatuses, roleStatus, statusLabel, closedNames } from '../lib/statuses.js';
import { renderParentContext } from './parent-context.js';
import { URGENCY_VALUES, urgencyCopy, urgencyLabel } from '../lib/task-urgency.js';
import { parseDayInput } from '../lib/due-tasks.js';
import {
  renderPermissionModeSelect, bindPermissionModeSelects, effectivePermissionModeHint, isCowboyAllowed,
  isPermissionMode, COWBOY_MODES, normalizeCowboyScope,
} from '../lib/agent-permission-mode.js';
import { t } from '../lib/i18n.js';
import {
  taskEditableFields, TASK_FIELD_GROUPS, AGENT_ASSIGNEES, EFFORT_LEVELS, MODEL_SUGGESTIONS,
  agentModelField, agentHasEffort, agentHasDesignMode, agentLabel, effortLabel, modelInputValue,
  storyPointsInputValue, dependencyFloor, applyDependencyFloor, blockingDependencyResults,
  dependencyProblemText, dependencyCheckPath,
} from '../lib/task-fields.js';

// ── Create task modal ──

// applyImportPrefill(overlay, { title, description }) — writes a picked import source's
// content (Trello card — C1448, Gmail thread — C1449) into the still-open New Task modal.
// Overwrites both fields (user decision — no merge with anything already typed).
function applyImportPrefill(overlay, { title, description }) {
  const titleEl = overlay.querySelector('#task-title');
  const descEl = overlay.querySelector('#task-desc');
  const previewEl = overlay.querySelector('#task-desc-preview');
  if (!titleEl || !descEl || !titleEl.isConnected) return; // modal closed while the fetch was in flight

  // maxlength="255" on #task-title is NOT enforced for a programmatic .value write, and the
  // API hard-rejects a >255-char title (400) on create. Truncate defensively so imported
  // source data cannot turn a valid modal submission into a validation error.
  if (title) {
    titleEl.value = title.slice(0, 255);
    // (C1512) titleEl carries its own 'input' -> persistDraft() listener (see
    // openCreateTaskModal's onMount below) — a programmatic .value write doesn't fire
    // 'input' on its own, so an imported card with a title but no description would
    // otherwise never get saved into the draft.
    titleEl.dispatchEvent(new Event('input', { bubbles: true }));
  }
  if (description) {
    descEl.value = description;
    // One synthetic input drives both C1446's preview re-render (the listener a few lines
    // below) and C1447's thumbnail strip (embed-menu.js's own 'input' listener).
    descEl.dispatchEvent(new Event('input', { bubbles: true }));
    // C1446 pins previewEl.style.height on every blur and never clears it; the preview box
    // is overflow-y:auto, so without clearing it here the freshly-imported (much longer)
    // content renders scrolled out of sight behind the old, smaller pinned height. C1446
    // re-pins it on the next blur, so its own button-jump protection is unaffected.
    if (previewEl && !previewEl.hidden) previewEl.style.height = '';
  }
}

// initialSprintPriority(priority, sprints, draftPriority) — the sprint the modal starts on.
// `defaultPriority` is what the hidden field submits (sprints off) and what an unparseable
// select saves into the draft; `restorePriority` is what the visible select pre-selects.
// A `priority` > 0 is an explicit sprint (a lane click, a scheduled parent) and beats a saved
// draft. 0 — a backlog parent — or absent means "no explicit sprint": both fall back to the
// newest sprint, so the visible pre-selection and the hidden submit can never disagree. A draft
// may restore Backlog (0) itself: the select offers it, so a user who picked it gets it back.
export function initialSprintPriority(priority, sprints, draftPriority) {
  const explicit = Number.isFinite(priority) && priority > 0;
  const defaultPriority = explicit ? priority : defaultSprintNumber(sprints);
  const restorePriority = explicit || draftPriority == null ? defaultPriority : draftPriority;
  return { defaultPriority, restorePriority };
}

const escapeAttr = s => escapeHtml(s).replace(/"/g, '&quot;');
const option = (value, label, selected) => `<option value="${escapeAttr(value)}"${selected ? ' selected' : ''}>${escapeHtml(label)}</option>`;

// One field's wrapper. `data-field` is its API field (lib/task-fields.js) — the parity test reads
// it — and `hidden` keeps a conditional control in the form so a toggle can reveal it in place.
function fieldHtml(key, label, control, { hidden = false, wide = false, labelFor = '', extra = '' } = {}) {
  const labelHtml = label ? `<label class="form-label"${labelFor ? ` for="${labelFor}"` : ''}>${escapeHtml(label)}</label>` : '';
  return `<div class="form-group task-form-field${wide ? ' task-form-field--wide' : ''}" data-field="${key}"${hidden ? ' hidden' : ''}>${labelHtml}${control}${extra}</div>`;
}

const message = (id, kind = 'note') => `<p class="task-form-${kind}" id="${id}" role="${kind === 'error' ? 'alert' : 'status'}" hidden></p>`;

// The markup of every create field, keyed by API field (renderCreateFields() iterates
// taskEditableFields(), so a catalogue entry without a renderer here fails loudly). `ctx` holds
// the values the form opens with.
const CREATE_FIELD_RENDERERS = {
  title: ctx => fieldHtml('title', `${t('taskField.title')} *`,
    `<input class="form-input" id="task-title" name="title" required maxlength="255" placeholder="${escapeAttr(t('createTask.titlePlaceholder'))}">`,
    { wide: true, labelFor: 'task-title' }),
  description: () => fieldHtml('description', t('taskField.description'),
    `<textarea class="form-textarea create-task-desc" id="task-desc" name="description"></textarea>
        <div class="md-preview create-task-desc-preview" id="task-desc-preview" title="Click to edit" hidden></div>`,
    { wide: true, labelFor: 'task-desc' }),
  type: ctx => fieldHtml('type', t('taskField.type'),
    `<select class="form-select" id="task-type" name="type">${ctx.allowedTypes.map(type => option(type, TYPE_LABELS[type] || type, type === ctx.type)).join('')}</select>`,
    { labelFor: 'task-type' }),
  status: ctx => fieldHtml('status', t('taskField.status'),
    `<select class="form-select" id="task-status" name="status">${ctx.statuses.map(s => option(s.name, statusLabel(s.name), s.name === ctx.status)).join('')}</select>`,
    { labelFor: 'task-status' }),
  category: ctx => fieldHtml('category', t('taskField.category'),
    `<select class="form-select" id="task-category" name="category">${['CODING', 'HUMAN'].map(c => option(c, t(`category.${c}`), c === ctx.category)).join('')}</select>`,
    { labelFor: 'task-category' }),
  // C1334 — sprints hidden: no sprint field; new tasks take defaultPriority, the value the field
  // would have pre-selected. The note under the select reports a dependency floor (TPT9213).
  priority: ctx => (ctx.showSprintField ? fieldHtml('priority', ctx.groupLabel,
    `<select class="form-select" id="task-priority" name="priority">
          ${ctx.sprintOpts}
          <option value="new">${escapeHtml(t('taskField.newSprint', { group: ctx.groupLabel }))}</option>
          ${ctx.sprints.length === 0
            ? `<option value="0" selected>${escapeHtml(ctx.groupLabel)} 1 (priority 0)</option>`
            : `<option value="0"${ctx.priority === 0 ? ' selected' : ''}>${escapeHtml(t('taskField.backlog'))}</option>`}
        </select>`,
    { labelFor: 'task-priority', extra: message('task-priority-note') }) : ''),
  assignee: ctx => fieldHtml('assignee', t('taskField.assignee'),
    `<div class="create-assignee-combo">${renderMemberCombo({ id: 'create-assignee', assignee: ctx.assignee, members: ctx.members })}</div>`),
  urgency: ctx => fieldHtml('urgency', urgencyCopy(ctx.language).field,
    `<select class="form-select" id="task-urgency" name="urgency">${URGENCY_VALUES.map(n => option(n, urgencyLabel(n, ctx.language), n === ctx.urgency)).join('')}</select>`,
    { labelFor: 'task-urgency' }),
  story_points: () => fieldHtml('story_points', t('taskField.storyPoints'),
    '<input class="form-input" id="task-story-points" name="story_points" type="number" min="0" step="1" inputmode="numeric" placeholder="—">',
    { labelFor: 'task-story-points', extra: message('task-story-points-error', 'error') }),
  due_date: () => fieldHtml('due_date', t('taskField.dueDate'),
    '<input class="form-input" id="task-due" name="due_date" type="date" max="9999-12-31">',
    { labelFor: 'task-due', extra: message('task-due-error', 'error') }),
  tags: () => fieldHtml('tags', t('taskField.tags'),
    '<input class="form-input" id="task-tags" placeholder="Add tag...">', { wide: true, labelFor: 'task-tags' }),
  dependencies: () => fieldHtml('dependencies', t('taskField.dependencies'),
    `<input class="form-input" id="task-deps" placeholder="${escapeAttr(t('deps.placeholder'))}">`,
    { wide: true, labelFor: 'task-deps', extra: message('task-deps-message', 'error') }),
  // A child entry point (Add Child, Epic Add Item) fixes the parent: the banner heading the form
  // shows it, and the create body carries its id. A top-level create picks one, or none.
  parent_id: ctx => (ctx.parentTask ? '' : fieldHtml('parent_id', t('taskField.parent'),
    `<input class="form-input" id="task-parent" placeholder="${escapeAttr(t('parent.placeholder'))}">`,
    { wide: true, labelFor: 'task-parent' })),
  is_objective: ctx => fieldHtml('is_objective', '',
    `<label class="task-form-check" for="task-objective">
          <input type="checkbox" id="task-objective" name="is_objective"${ctx.isObjective ? ' checked' : ''}>
          <span class="task-form-check-text"><span class="task-form-check-label">${escapeHtml(t('taskField.objective'))}</span>
          <span class="task-form-hint">${escapeHtml(t('taskField.objectiveHint'))}</span></span>
        </label>`, { wide: true }),
  agent_assignee: ctx => fieldHtml('agent_assignee', t('taskField.agent'),
    `<select class="form-select" id="task-agent" name="agent_assignee">${['', ...AGENT_ASSIGNEES].map(a => option(a, agentLabel(a), a === (ctx.agent || ''))).join('')}</select>`,
    { labelFor: 'task-agent' }),
  claude_model: ctx => modelFieldHtml('claude', ctx),
  codex_model: ctx => modelFieldHtml('codex', ctx),
  pi_model: ctx => modelFieldHtml('pi', ctx),
  effort: ctx => fieldHtml('effort', t('taskField.effort'),
    `<select class="form-select" id="task-effort" name="effort">${['', ...EFFORT_LEVELS].map(e => option(e, effortLabel(e), e === (ctx.effort || ''))).join('')}</select>`,
    { labelFor: 'task-effort', hidden: !agentHasEffort(ctx.agent) }),
  claude_design_mode: ctx => fieldHtml('claude_design_mode', t('taskField.designMode'),
    `<label class="task-form-toggle" for="task-design-mode">
          <input type="checkbox" id="task-design-mode" name="claude_design_mode"${ctx.designMode ? ' checked' : ''}>
          <span class="task-form-hint">${escapeHtml(t('taskField.designModeHint'))}</span>
        </label>`, { hidden: !agentHasDesignMode(ctx.agent) }),
  agent_permission_mode: ctx => fieldHtml('agent_permission_mode', t('taskField.permissionMode'),
    renderPermissionModeSelect(ctx.permissionMode, { id: 'task-permission-mode', className: 'task-permission-mode-input', projectDefault: { inherited: ctx.inheritedMode } }),
    { wide: true, extra: `<p class="task-form-hint" id="task-permission-hint">${escapeHtml(effectivePermissionModeHint(ctx.permissionMode, ctx.inheritedMode))}</p>` }),
  cowboy_mode: ctx => {
    const allowed = isCowboyAllowed(ctx.permissionMode, ctx.inheritedMode);
    return fieldHtml('cowboy_mode', t('taskField.cowboyMode'),
      `<select class="form-select" id="task-cowboy" name="cowboy_mode" aria-describedby="task-cowboy-hint">${COWBOY_MODES.map(m => option(m, t(`cowboyMode.${m}`), m === (allowed ? ctx.cowboy : 'disabled'))).join('')}</select>`,
      { labelFor: 'task-cowboy', hidden: !allowed, wide: true, extra: `<p class="task-form-hint" id="task-cowboy-hint">${escapeHtml(t('cowboyMode.hint'))}</p>` });
  },
};

function modelFieldHtml(agent, ctx) {
  const key = agentModelField(agent);
  const id = `task-model-${agent}`;
  return fieldHtml(key, t('taskField.model'),
    `<input class="form-input task-model-input" id="${id}" name="${key}" data-agent="${agent}" list="${id}-list" maxlength="200" autocomplete="off" placeholder="${escapeAttr(t('taskField.modelPlaceholder'))}" value="${escapeAttr(ctx.models[agent] || '')}">
        <datalist id="${id}-list">${MODEL_SUGGESTIONS[agent].map(m => `<option value="${escapeAttr(m)}"></option>`).join('')}</datalist>`,
    { labelFor: id, hidden: ctx.agent !== agent });
}

// Every editable field's markup in its section, in catalogue order (lib/task-fields.js). Exported
// as the parity test's seam: each create-able field must render a `data-field` wrapper.
export function renderCreateFields(ctx) {
  const fields = taskEditableFields().filter(f => f.create);
  const html = {};
  for (const group of TASK_FIELD_GROUPS) {
    html[group] = fields.filter(f => f.group === group).map((f) => {
      const render = CREATE_FIELD_RENDERERS[f.key];
      if (!render) throw new Error(`No New Task control for task field "${f.key}"`);
      return render(ctx);
    }).join('');
  }
  const agentOpen = !!(ctx.agent || ctx.effort || ctx.designMode || ctx.permissionMode
    || Object.values(ctx.models).some(Boolean) || normalizeCowboyScope(ctx.cowboy) !== 'disabled');
  return `
      <div class="task-form-main">${html.main}</div>
      <div class="task-form-grid" data-section="details">${html.details}</div>
      <div class="task-form-stack" data-section="links">${html.links}</div>
      <details class="task-form-section" data-section="agent"${agentOpen ? ' open' : ''}>
        <summary class="task-form-section-summary">
          <span class="task-form-section-title">${escapeHtml(t('taskField.agentSection'))}</span>
          <span class="task-form-section-value" id="task-agent-summary">${escapeHtml(agentLabel(ctx.agent))}</span>
        </summary>
        <p class="task-form-hint">${escapeHtml(t('taskField.agentHint'))}</p>
        <div class="task-form-grid">${html.agent}</div>
      </details>`;
}

// opts.priority (TPT30) — an explicit sprint to prefill, e.g. from the sprint board's own
// per-lane "+ Add item" tile (sprint-board.js's onAddTask), or a parent's sprint (epic Add
// Item, drawer Add Child). Omitted for the header's "+ New Task" button; omitted and 0 (a
// backlog parent) both keep the default-newest-sprint behavior — see initialSprintPriority().
// opts.status — an explicit status (a kanban column); opts.allowedTypes narrows Task/Story/Bug
// (a Story parent passes Task and Bug).
//
// Resolves to the modal's owner token (ui.js openModal(), TPT647) for the caller's teardown, or
// null when it never opened (form data failed to load, or the caller's page is gone).
export async function openCreateTaskModal(projectId, opts = {}) {
  // Capture per-open context; late responses must never read a different page's state.
  const routePath = () => window.location.hash.split('?')[0];
  const initialRoute = routePath();
  const isAlive = () => routePath() === initialRoute && (!opts.isAlive || opts.isAlive());
  let state;
  try {
    const [project, sprintRows, members, sprints, statuses] = await Promise.all([
      opts.project !== undefined ? opts.project : loadProject(projectId),
      // Sprint numbers only — `{ priority }` rows for the Sprint options and the "+ New" number.
      // Never the task list: GET /tasks/facets `priorities` is project-wide whatever the caller
      // holds. Not fatal: without it the sprint table alone still yields options.
      opts.sprintRows || opts.tasks || api.get(`/projects/${projectId}/tasks/facets`)
        .then(r => (r.priorities || []).map(priority => ({ priority })))
        .catch(() => []),
      opts.members || api.get(`/projects/${projectId}/members`).then(r => r.members || []),
      opts.sprints || api.get(`/projects/${projectId}/sprints`).then(r => r.sprints || []),
      opts.statuses || loadStatuses(projectId),
    ]);
    state = { projectId, project, sprintRows, members, sprints, statuses };
  } catch (err) {
    if (isAlive()) showToast(err.message || t('createTask.loadFailed'), 'error');
    return null;
  }
  if (!isAlive()) return null;
  const parentTask = opts.parentTask || opts.parentEpic || null;
  const draftScope = parentTask
    ? `${parentTask.type === 'epic' ? 'epic' : 'parent'}:${parentTask.id}`
    : null;
  // (C1512) Restore an in-progress draft for this project, if one survived a previous
  // accidental dismissal (Escape/backdrop/navigation) — see lib/task-draft.js. null when
  // there's nothing saved, or storage held only garbage.
  const draft = loadNewTaskDraft(state.projectId, draftScope);
  const restoredStatus = state.statuses.some(s => s.name === draft?.status) ? draft.status : null;
  const initialStatus = opts.status || restoredStatus || roleStatus(state.statuses, 'start')?.name || 'pending';
  const supportedTypes = ['task', 'story', 'bug'];
  const allowedTypes = Array.isArray(opts.allowedTypes)
    ? [...new Set(opts.allowedTypes)].filter(type => supportedTypes.includes(type))
    : supportedTypes;
  if (allowedTypes.length === 0) allowedTypes.push('task');
  const requestedType = draft?.type || opts.type || 'task';
  const initialType = allowedTypes.includes(requestedType) ? requestedType : allowedTypes[0];
  const closedSet = new Set(closedNames(state.statuses));
  const language = state.project?.language;
  const inheritedMode = isPermissionMode(state.project?.agent_permission_mode) ? state.project.agent_permission_mode : null;

  const singular = groupLabelSingular(state.project && state.project.task_group_label);
  // (C1512) A restored sprint number that no longer matches any real sprint/task priority
  // self-heals: sprintOptionsHtml() adds `currentPriority` to its option set whenever it
  // isn't already present, so this can never render a <select> with no selected option.
  // An explicit lane click (opts.priority > 0) always wins over a stale saved draft's sprint —
  // the user just told us which lane they meant. A restored 0 selects the Backlog option.
  const { defaultPriority, restorePriority } = initialSprintPriority(opts.priority, state.sprints, draft?.priority);
  const sprintOpts = sprintOptionsHtml(state.sprints, state.sprintRows, restorePriority, singular);
  const showSprintField = sprintsEnabled(state.project);

  let closed = false;
  let submitting = false;
  // (C1512) A restored assignee id is trusted only when it's still a real project member
  // (or members haven't loaded yet, in which case there's nothing to validate against) —
  // a member removed from the project since the draft was saved falls back to self, same
  // as the no-draft default below.
  const knownMemberIds = new Set(state.members.map(m => m.user_id || m.id));
  const restoredAssignee = draft && draft.assignee != null
    && (knownMemberIds.size === 0 || knownMemberIds.has(draft.assignee))
    ? draft.assignee : null;
  // New tasks default to the calling user in this form. Picking Unassigned remains
  // reachable because submit always sends the committed value, including null.
  let assignee = restoredAssignee ?? ((getUser() || {}).id ?? null);
  // Every top-level web create is an objective, a child never is (C1341) — the default of the
  // Objective checkbox, which follows the Parent field until the user sets it themselves.
  let objectiveTouched = typeof draft?.is_objective === 'boolean';
  const fieldCtx = {
    language, statuses: state.statuses, members: state.members, sprints: state.sprints,
    allowedTypes, type: initialType, status: initialStatus, category: draft?.category || 'CODING',
    showSprintField, groupLabel: singular, sprintOpts, priority: restorePriority, assignee,
    urgency: draft?.urgency ?? 0, parentTask,
    isObjective: objectiveTouched ? draft.is_objective : !(parentTask || draft?.parent),
    agent: draft?.agent_assignee || null,
    models: { claude: draft?.claude_model || '', codex: draft?.codex_model || '', pi: draft?.pi_model || '' },
    effort: draft?.effort || null, designMode: !!draft?.claude_design_mode,
    permissionMode: draft?.agent_permission_mode || null, inheritedMode,
    cowboy: draft?.cowboy_mode || 'disabled',
  };

  let assigneeCombo = null;
  let embedControls = null;
  let tagTypeahead = null;
  let depTypeahead = null;
  let parentTypeahead = null;
  let unbindPermissionModes = null;
  let depCheckSeq = 0;
  // (C1512) Attachments uploaded into THIS draft's description — tracked separately from
  // the description text itself (which only ever derives the visible thumbnail strip, see
  // lib/attachment-preview.js) so discardDraft() below deletes exactly what this modal
  // created, never an image/file ref the user hand-pasted in from somewhere else.
  let uploads = draft && draft.uploads ? draft.uploads.slice() : [];
  // Set at the top of onMount so persistDraft()/discardDraft() can read live field values
  // without threading `overlay` through every helper individually.
  let modalOverlay = null;
  // Own route cleanup as well as page cleanup: async routes may not run teardown.
  const onRouteChange = () => {
    if (routePath() !== initialRoute && modalOverlay?.isConnected) closeModal();
  };

  const body = `
    <form id="create-task-form" class="task-form">
      ${renderParentContext(parentTask)}
      ${renderCreateFields(fieldCtx)}
      <div class="modal-footer">
        <button type="button" class="btn btn-ghost" id="modal-cancel">${escapeHtml(t('createTask.cancel'))}</button>
        <button type="submit" class="btn btn-primary" id="create-task-btn">${escapeHtml(t('createTask.submit'))}</button>
      </div>
    </form>`;

  const $ = sel => modalOverlay && modalOverlay.querySelector(sel);

  // The selected agent ('' = project default) and its dependent controls' visibility.
  function selectedAgent() {
    return $('#task-agent')?.value || null;
  }

  // The parent the new task is created under: the entry point's, else the picked one.
  function chosenParent() {
    if (parentTask) return parentTask;
    const key = parentTypeahead ? parentTypeahead.getKeys()[0] : null;
    return key ? parentTypeahead.getEntry(key) || null : null;
  }

  // The sprint the select shows, as a number ('new' → the next one); defaultPriority when the
  // field is hidden or holds nothing parseable.
  function selectedPriority() {
    if (!showSprintField) return defaultPriority;
    const raw = $('#task-priority')?.value;
    if (raw === 'new') return nextSprintNumber(state.sprints, state.sprintRows);
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : defaultPriority;
  }

  // (C1512) Snapshot every live field into localStorage. Bound to each field's own change
  // event from onMount, AFTER the draft-restore prefill runs there — so reopening a draft
  // can never immediately resave it back as empty.
  function persistDraft() {
    if (!modalOverlay || closed) return;
    let priority = defaultPriority;
    const priorityEl = $('#task-priority');
    if (showSprintField && priorityEl) {
      const n = parseInt(priorityEl.value, 10);
      // priorityEl.value === 'new' (unconfirmed "+ New Sprint") parses to NaN — fall back
      // to defaultPriority rather than persist a not-yet-real sprint number.
      if (Number.isFinite(n)) priority = n;
    }
    const parent = parentTask ? null : chosenParent();
    const permission = $('#task-permission-mode')?.value || null;
    saveNewTaskDraft(state.projectId, {
      title: $('#task-title')?.value || '',
      description: $('#task-desc')?.value || '',
      tags: tagTypeahead ? tagTypeahead.getTags() : [],
      dependencies: depTypeahead ? depTypeahead.getKeys() : [],
      category: $('#task-category')?.value || 'CODING',
      status: $('#task-status')?.value,
      type: $('#task-type')?.value,
      assignee,
      priority,
      uploads,
      urgency: Number($('#task-urgency')?.value) || 0,
      story_points: $('#task-story-points')?.value || '',
      due_date: $('#task-due')?.value || '',
      parent: parent ? { id: parent.id, task_key: parent.task_key, title: parent.title || '', type: parent.type } : null,
      ...(objectiveTouched ? { is_objective: !!$('#task-objective')?.checked } : {}),
      agent_assignee: selectedAgent(),
      claude_model: modelInputValue($('#task-model-claude')?.value),
      codex_model: modelInputValue($('#task-model-codex')?.value),
      pi_model: modelInputValue($('#task-model-pi')?.value),
      effort: $('#task-effort')?.value || null,
      claude_design_mode: !!$('#task-design-mode')?.checked,
      agent_permission_mode: permission,
      cowboy_mode: $('#task-cowboy')?.value || 'disabled',
    }, draftScope);
  }

  // recordUpload({kind, url}) — passed as onUpload to both upload paths (paste/drop via
  // attachImageHandlers, the Embed picker via attachEmbedControls) so this modal always
  // knows exactly which attachments it created, regardless of which path produced them.
  function recordUpload(upload) {
    uploads.push(upload);
    persistDraft();
  }

  // discardDraft() — the user explicitly said "throw this away" (Cancel or the header ×,
  // wired in onMount below): erase the stored draft AND delete every attachment it
  // uploaded. Fire-and-forget — a 404 from an
  // already-deleted row is fine, there's nothing useful to do with a failure here.
  function discardDraft() {
    if (submitting) return;
    clearNewTaskDraft(state.projectId, draftScope);
    for (const u of uploads) {
      const path = attachmentDeletePath(u.url);
      if (path) api.delete(path).catch(() => {});
    }
    uploads = [];
  }

  // Shows (text) or hides (no text) one of the form's inline messages.
  function setMessage(id, text) {
    const el = $(`#${id}`);
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }

  // Agent-specific controls follow the Agent select: the model input for that agent, Effort for
  // Claude/Codex, Design mode for Claude. Hidden controls keep their values.
  function syncAgentFields() {
    const agent = selectedAgent();
    for (const field of ['claude_model', 'codex_model', 'pi_model']) {
      const wrapper = $(`[data-field="${field}"]`);
      if (wrapper) wrapper.hidden = agentModelField(agent) !== field;
    }
    const effort = $('[data-field="effort"]');
    if (effort) effort.hidden = !agentHasEffort(agent);
    const design = $('[data-field="claude_design_mode"]');
    if (design) design.hidden = !agentHasDesignMode(agent);
    const summary = $('#task-agent-summary');
    if (summary) summary.textContent = agentLabel(agent);
  }

  // Cowboy scope shows only while the effective permission mode is Bypass (the API stores
  // Disabled otherwise); the hint explains the effective mode.
  function syncPermissionFields() {
    const mode = $('#task-permission-mode')?.value || null;
    const allowed = isCowboyAllowed(mode, inheritedMode);
    const cowboy = $('[data-field="cowboy_mode"]');
    if (cowboy) cowboy.hidden = !allowed;
    if (!allowed && $('#task-cowboy')) $('#task-cowboy').value = 'disabled';
    const hint = $('#task-permission-hint');
    if (hint) hint.textContent = effectivePermissionModeHint(mode, inheritedMode);
  }

  // The Objective checkbox follows the parent (none → objective) until the user ticks it.
  function syncObjectiveDefault() {
    const box = $('#task-objective');
    if (box && !objectiveTouched) box.checked = !chosenParent();
  }

  // Dependency entries known so far — search rows the chips were picked from, plus whatever a
  // dependency check added.
  function dependencyEntries() {
    if (!depTypeahead) return [];
    return depTypeahead.getKeys().map(key => depTypeahead.getEntry(key) || { task_key: key });
  }

  // The live note under the Sprint select: a dependency in this sprint or a later one moves the
  // new task to the sprint after it on save (the floor every create path applies).
  function syncFloorNote() {
    if (!showSprintField) return;
    const { moved } = applyDependencyFloor(selectedPriority(), dependencyFloor(dependencyEntries()));
    setMessage('task-priority-note', moved
      ? t('deps.floorCreate', { key: moved.key, group: singular, priority: moved.depPriority, floor: moved.to })
      : '');
  }

  // Resolves the chips through GET /tasks/dependency-check (titles, status, sprint, problems).
  // Resolves the results, or null when the check failed or a newer one superseded it.
  async function checkDependencies() {
    const keys = depTypeahead ? depTypeahead.getKeys() : [];
    if (keys.length === 0) return [];
    const mySeq = ++depCheckSeq;
    const parent = chosenParent();
    const path = dependencyCheckPath(state.projectId, keys, { parentId: parent && Number.isInteger(parent.id) ? parent.id : null });
    let reply;
    try {
      reply = await api.get(path);
    } catch (err) {
      if (mySeq === depCheckSeq && !closed) setMessage('task-deps-message', t('deps.checkFailed', { error: err.message || 'Request failed' }));
      return null;
    }
    if (mySeq !== depCheckSeq || closed) return null;
    const results = (reply && reply.dependencies) || [];
    depTypeahead.setInfo(results);
    return results;
  }

  // Reports problems on the chips' check results; returns whether any blocks the create.
  function reportDependencyProblems(results) {
    const blocking = blockingDependencyResults(results, []);
    setMessage('task-deps-message', blocking.length
      ? [...blocking.map(dependencyProblemText), t('deps.blockedCreate')].join(' ')
      : '');
    syncFloorNote();
    return blocking.length > 0;
  }

  return openModal(t('createTask.title'), body, {
    onMount(overlay) {
      modalOverlay = overlay; // (C1512) lets persistDraft()/discardDraft() read live fields
      overlay.querySelector('.modal')?.classList.add('modal-task-form');
      window.addEventListener('hashchange', onRouteChange);
      const scrollEl = overlay.querySelector('.modal') || overlay;

      const comboRoot = overlay.querySelector('.create-assignee-combo');
      assigneeCombo = createMemberCombo(comboRoot, {
        getMembers: () => state.members, // live — page-load members fetch may still be in flight
        getValue: () => assignee,
        setValue: (id) => { assignee = id; persistDraft(); }, // (C1512)
        onCommit: () => {}, // nothing to PATCH until submit — the row isn't finalized yet
        isAlive: () => document.body.contains(comboRoot),
        // overlay is ui.js's fixed full-viewport .modal-overlay; the real scroller is
        // its .modal child (max-height: 85vh; overflow-y: auto) — same as the Add Child
        // modal's combo in task-detail.js.
        scrollEl,
      });

      // Existing tags only (C1511) — a name typed here that isn't already registered has
      // no way to become a chip, so the create request below can never 400 with
      // TAGS_UNREGISTERED (resolveTagIds(), api/src/lib/task-service.js) again. Creating a
      // brand-new tag stays a task-detail-panel action (its own typeahead instance passes
      // allowCreate:true) — this modal has no room for the required-description form.
      tagTypeahead = attachTagTypeahead(overlay.querySelector('#task-tags'), {
        projectId: state.projectId,
        initialTags: draft && draft.tags ? draft.tags : [], // (C1512) restore draft chips
        scrollEl,
        allowCreate: false,
        onChange: () => persistDraft(), // (C1512)
        isAlive: () => document.body.contains(overlay),
      });

      // Depends on (TPT598) — candidates come from an on-demand server search, so a task
      // outside the caller's rows (e.g. the board's filtered page) is still reachable. Each pick
      // carries its sprint and status, so the floor note updates without a request; submit
      // re-checks the whole list (TPT9213).
      depTypeahead = attachDependencyTypeahead(overlay.querySelector('#task-deps'), {
        projectId: state.projectId,
        initialKeys: draft && draft.dependencies ? draft.dependencies : [],
        scrollEl,
        statusText: statusLabel,
        isClosed: name => closedSet.has(name),
        onChange: () => {
          // A removed chip may have been the one reported: re-derive the report from what's left.
          if (!$('#task-deps-message')?.hidden) reportDependencyProblems(dependencyEntries());
          syncFloorNote();
          persistDraft();
        },
        isAlive: () => document.body.contains(overlay),
      });

      // Parent (top-level creates only): one task, searched like Depends on.
      if (!parentTask) {
        parentTypeahead = attachDependencyTypeahead(overlay.querySelector('#task-parent'), {
          projectId: state.projectId,
          initialKeys: draft?.parent ? [draft.parent.task_key] : [],
          initialInfo: draft?.parent ? [draft.parent] : [],
          max: 1,
          showTitles: true,
          scrollEl,
          statusText: statusLabel,
          isClosed: name => closedSet.has(name),
          onChange: () => { syncObjectiveDefault(); persistDraft(); },
          isAlive: () => document.body.contains(overlay),
        });
      }

      // Wire paste/drop upload immediately. New-task attachments have no task key until
      // the create request commits; their markdown URLs still identify the stored rows.
      // allowFiles:true (C1447) — this field also gets the Embed ▾ picker below, so
      // dropping/pasting a non-image file must work too, not just images.
      const descEl = overlay.querySelector('#task-desc');
      if (descEl) attachImageHandlers(descEl, state.projectId, null, { allowFiles: true, onUpload: recordUpload });

      // Markdown edit/preview toggle (C1446) — mirrors task-detail.js's click-to-edit/
      // blur-to-render pattern, but keeps `descEl` in the DOM (never replaceWith) since
      // the submit handler below reads it as `form.description.value`: swapping it out
      // for a rendered div would make that a form.elements miss. Preview shown only on
      // blur with non-empty content — an empty preview box in a create form is just noise.
      const previewEl = overlay.querySelector('#task-desc-preview');
      if (descEl && previewEl) {
        descEl.addEventListener('blur', () => {
          if (!descEl.value.trim()) return;
          // Pin the swapped-in element's height to the outgoing one's BEFORE toggling
          // hidden, in both directions — the fields below would jump under a still-in-flight
          // click (blur fires on the next control's own mousedown) if the box resized.
          previewEl.style.height = descEl.offsetHeight + 'px';
          previewEl.innerHTML = renderMarkdown(descEl.value);
          descEl.hidden = true;
          previewEl.hidden = false;
        });
        previewEl.addEventListener('click', () => {
          descEl.style.height = previewEl.offsetHeight + 'px';
          previewEl.hidden = true;
          descEl.hidden = false;
          descEl.focus();
          const len = descEl.value.length;
          descEl.setSelectionRange(len, len);
        });
        // Async image upload (lib/image-paste.js) rewrites descEl.value and dispatches
        // 'input' after blur may already have rendered the preview from the placeholder
        // text — re-render so the preview never shows a stale "uploading..." ref.
        descEl.addEventListener('input', () => {
          if (!previewEl.hidden) previewEl.innerHTML = renderMarkdown(descEl.value);
        });
      }

      // Embed ▾ button + live thumbnail strip (C1447) — anchored after previewEl (not
      // descEl) so the block sits below whichever of textarea/preview C1446's toggle is
      // currently showing, rather than wedging between them. Kept visible through both
      // toggle states rather than hidden on blur: hiding it would shrink the field by the
      // strip's height at the exact moment blur fires — the same jump C1446's
      // height-pinning exists to prevent, reintroduced one element lower.
      if (descEl && previewEl) {
        embedControls = attachEmbedControls(descEl, {
          projectId: state.projectId,
          taskKey: null,
          anchor: previewEl,
          scrollEl,
          onUpload: recordUpload, // (C1512)
          // Import ▾ (C1448) — Task App sub-nav order (Jira / Trello / Gmail). All three
          // now open their own picker: Trello (C1448), Gmail (C1449), Jira (C1450).
          imports: [
            { action: 'jira', label: 'From Jira' },
            { action: 'trello', label: 'From Trello' },
            { action: 'gmail', label: 'From Gmail' },
          ],
          onImport: (action) => {
            // .member-combo-dropdown is z-index 320, above the picker's 260 — with
            // .embed-block's mousedown->preventDefault the assignee input never blurs on
            // its own, so an open combo would paint over the picker. Same for the tag and
            // task typeaheads' own dropdowns (z-index 320 too).
            assigneeCombo?.close();
            tagTypeahead?.close();
            depTypeahead?.close();
            parentTypeahead?.close();
            const onPick = (payload) => applyImportPrefill(overlay, payload);
            if (action === 'trello') { openTrelloCardPicker(state.projectId, { onPick }); return; }
            if (action === 'gmail') { openGmailThreadPicker({ onPick }); return; }
            if (action === 'jira') { openJiraIssuePicker({ onPick }); return; }
          },
        });
      }

      // (C1512) Restore prefill — placed after attachEmbedControls() above so the
      // synthetic 'input' dispatch below reaches its 'input' listener
      // (renderAttachmentStrip) and repaints the thumbnail strip for any restored
      // image/file refs already sitting in the description text. Values never pass through
      // the markup template (no escaping hazard on user text).
      const titleEl = overlay.querySelector('#task-title');
      if (draft) {
        if (draft.title) titleEl.value = draft.title;
        if (draft.description && descEl) {
          descEl.value = draft.description;
          descEl.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (draft.story_points != null && draft.story_points !== '' && $('#task-story-points')) $('#task-story-points').value = String(draft.story_points);
        if (draft.due_date && $('#task-due')) $('#task-due').value = draft.due_date;
      }

      // Permission mode listbox (keyboard + pointer), shared with the drawer and Settings.
      unbindPermissionModes = bindPermissionModeSelects(overlay);

      // (C1512) Persist on every field change — bound AFTER the restore above, so
      // reopening a draft can never immediately resave it back as empty.
      for (const sel of ['#task-type', '#task-status', '#task-category', '#task-urgency', '#task-due', '#task-effort', '#task-design-mode', '#task-cowboy']) {
        overlay.querySelector(sel)?.addEventListener('change', persistDraft);
      }
      for (const sel of ['#task-title', '#task-story-points', '.task-model-input']) {
        overlay.querySelectorAll(sel).forEach(el => el.addEventListener('input', persistDraft));
      }
      if (descEl) descEl.addEventListener('input', persistDraft);
      overlay.querySelector('#task-priority')?.addEventListener('change', () => { syncFloorNote(); persistDraft(); });
      overlay.querySelector('#task-agent')?.addEventListener('change', () => { syncAgentFields(); persistDraft(); });
      overlay.querySelector('#task-permission-mode')?.addEventListener('change', () => { syncPermissionFields(); persistDraft(); });
      overlay.querySelector('#task-objective')?.addEventListener('change', () => { objectiveTouched = true; persistDraft(); });
      overlay.querySelector('#task-story-points')?.addEventListener('input', () => setMessage('task-story-points-error', ''));
      overlay.querySelector('#task-due')?.addEventListener('change', () => setMessage('task-due-error', ''));

      // A restored draft's dependencies are only keys: resolve them once so their chips show
      // titles and status, and a task deleted since is reported before submit.
      if (depTypeahead.getKeys().length) checkDependencies().then(results => { if (results) reportDependencyProblems(results); });
      syncFloorNote();

      overlay.querySelector('#modal-cancel').addEventListener('click', () => { discardDraft(); closeModal(); });
      // (C1512) Capture-phase — openModal() registers #modal-close's own bubble-phase
      // `click -> closeModal()` listener BEFORE onMount runs, so a bubble-phase listener
      // added here would fire too late (closeModal() already ran). Capture always
      // completes before any bubble-phase handler anywhere fires, regardless of
      // registration order — same trick embed-menu.js uses for its Escape handler. The ×
      // is an explicit "I'm done with this" action, same as Cancel: erase the draft.
      overlay.addEventListener('click', (e) => {
        if (e.target.closest('#modal-close')) discardDraft();
      }, true);
      overlay.querySelector('#create-task-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        if (submitting || closed) return;

        const form = e.target;
        // Field checks first; a failure keeps every value and focuses the field to fix.
        const pointsEl = $('#task-story-points');
        const points = pointsEl && pointsEl.validity && pointsEl.validity.badInput
          ? storyPointsInputValue('x')
          : storyPointsInputValue(pointsEl ? pointsEl.value : '');
        if (points.error) {
          setMessage('task-story-points-error', points.error);
          $('#task-story-points')?.focus();
          return;
        }
        const dueEl = $('#task-due');
        const dueDate = dueEl && dueEl.validity && dueEl.validity.badInput ? undefined : parseDayInput(dueEl ? dueEl.value : '');
        if (dueDate === undefined) {
          setMessage('task-due-error', t('taskField.dueDateInvalid'));
          dueEl?.focus();
          return;
        }

        let priority;
        if (!showSprintField) {
          // C1334 — field hidden → submit exactly what it would have pre-selected. Never let a
          // hidden control silently change what gets submitted.
          priority = defaultPriority;
        } else {
          const priorityVal = form.priority.value;
          if (priorityVal === 'new') {
            // One past every listed sprint: an empty sprint-table row can sit above the
            // highest in-use number, so the in-use numbers alone could name a listed sprint.
            priority = nextSprintNumber(state.sprints, state.sprintRows);
          } else {
            priority = parseInt(priorityVal, 10);
          }
        }
        const tagsRaw = tagTypeahead ? tagTypeahead.getTags() : [];
        const deps = depTypeahead ? depTypeahead.getKeys() : [];
        const parent = chosenParent();

        const btn = overlay.querySelector('#create-task-btn');
        btn.disabled = true;
        btn.textContent = t('createTask.submitting');
        submitting = true;
        const restoreButton = () => {
          btn.disabled = false;
          btn.textContent = t('createTask.submit');
        };

        try {
          // Dependencies are checked before anything is written (TPT9213): a task deleted since
          // it was picked, or a parent of the new task, is reported on its chip and the form
          // stays as it is. A check that cannot run falls back to what the picks carried.
          let depEntries = dependencyEntries();
          if (deps.length) {
            const results = await checkDependencies();
            if (closed) return;
            if (results) {
              if (reportDependencyProblems(results)) {
                restoreButton();
                depTypeahead.focus({ open: false });
                $('#task-deps-message')?.scrollIntoView({ block: 'nearest' });
                return;
              }
              depEntries = results;
            }
          }
          // The dependency floor: an open dependency in this sprint or a later one moves the new
          // task to the sprint after it — the live note under the Sprint select said so.
          priority = applyDependencyFloor(priority, dependencyFloor(depEntries)).priority;

          const agent = selectedAgent();
          const permissionMode = $('#task-permission-mode')?.value || null;
          const modelField = agentModelField(agent);
          // task_key is deliberately omitted. POST allocates and inserts it in one
          // transaction, so opening, canceling, or failing validation consumes no key.
          const { task } = await api.post(`/projects/${state.projectId}/tasks`, {
            title: form.title.value.trim(),
            description: form.description.value,
            category: form.category.value,
            priority,
            tags: tagsRaw.length ? tagsRaw : undefined,
            dependencies: deps.length ? deps : undefined,
            // Always sent (never conditional) so picking Unassigned writes null.
            assignee,
            status: form.status.value,
            type: form.type ? form.type.value : 'task',
            parent_id: parent && Number.isInteger(parent.id) ? parent.id : null,
            is_objective: !!$('#task-objective')?.checked,
            urgency: Number(form.urgency.value) || 0,
            story_points: points.value,
            due_date: dueDate,
            // null = the project's default agent; a HUMAN category then defaults to `human`.
            agent_assignee: agent,
            // Only the selected agent's settings are sent: a hidden control keeps its value for
            // a switch back, never for the saved row.
            claude_model: modelField === 'claude_model' ? modelInputValue($('#task-model-claude')?.value) : null,
            codex_model: modelField === 'codex_model' ? modelInputValue($('#task-model-codex')?.value) : null,
            pi_model: modelField === 'pi_model' ? modelInputValue($('#task-model-pi')?.value) : null,
            effort: agentHasEffort(agent) ? ($('#task-effort')?.value || null) : null,
            claude_design_mode: agentHasDesignMode(agent) && !!$('#task-design-mode')?.checked,
            agent_permission_mode: permissionMode,
            cowboy_mode: isCowboyAllowed(permissionMode, inheritedMode) ? ($('#task-cowboy')?.value || 'disabled') : 'disabled',
          });
          // (C1512) Clear only — never discardDraft(): the uploads tracked in `uploads`
          // now belong to the just-created task and must survive.
          clearNewTaskDraft(state.projectId, draftScope);
          if (!closed) closeModal();
          showToast(t('createTask.created'), 'success');
          if (isAlive()) await opts.onCreated?.(task);
        } catch (err) {
          restoreButton();
          showToast(err.message || t('createTask.failed'), 'error');
        } finally {
          submitting = false;
        }
      });
    },
    onClose() {
      closed = true;
      window.removeEventListener('hashchange', onRouteChange);
      // Destroy first, unconditionally — leaked scroll/resize listeners + a stray
      // body-appended dropdown node would otherwise survive a successful create too
      // (mirrors task-detail.js's Add Child modal onClose).
      if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
      if (tagTypeahead) { tagTypeahead.destroy(); tagTypeahead = null; }
      if (depTypeahead) { depTypeahead.destroy(); depTypeahead = null; }
      if (parentTypeahead) { parentTypeahead.destroy(); parentTypeahead = null; }
      if (embedControls) { embedControls.destroy(); embedControls = null; }
      if (unbindPermissionModes) { unbindPermissionModes(); unbindPermissionModes = null; }
      // Orphan-overlay guard (C1448, generalized C1449): if this modal is closing while an
      // Import ▾ picker (Trello, Gmail, Jira) is still open above it (Escape reaching this
      // modal, backdrop click, route navigation), nothing else would tear the picker down
      // — it would survive as a full-viewport overlay with a live capture-phase Escape
      // handler. The shared singleton in lib/import-picker-shell.js means this one call
      // covers every picker kind. No-ops if none open.
      closeImportPicker();
    },
  });
}
